/**
 * ARENA KIT — shared realism pass for the three day "training" maps
 * (grid_arena, arena_xl, sandlot). 2026-11-04, owner: "improve each map",
 * "realistic", "get inspired by existing models, don't copy them".
 *
 * Before this pass the arenas were grey plaster boxes on a flat slab and the
 * practice field was flat-coloured crates on grass. Inspiration (not copies):
 * military/police training ranges and municipal sports grounds — stacked
 * precast "lego" barrier blocks with lifting eyes, steel corner guards and
 * hazard-striped feet; LED perimeter advertising boards; floodlight masts;
 * covered concrete grandstands with tip-up seats; a stadium scoreboard;
 * painted court lines and spawn zones. The practice field gets a
 * football pitch in chalk, a log-palisade on the forts with a painted
 * practice goal, nailed crates, precast blocks and a wall of numbered
 * shooting targets on straw butts. (Boxes the minor-wall pass already caps
 * with stone are left to it — see `minorDressed`.)
 *
 * Structure (as compound.ts / neon_district.ts): `arenaLayout` / `fieldLayout`
 * are PURE (no three.js objects, no DOM) and return pieces + a floor-paint
 * program, so `agent/bench-extras-arenas.mjs` checks the collision contract
 * against the real shared boxes. `buildKit` turns the pieces into ONE merged
 * mesh per material (≤ 10 draw calls incl. the floor paint plane), once, at
 * load. No lights, no per-frame work.
 *
 * Zones (the contract the bench enforces):
 *  - `skin`  ON a side face of its host collider, ≤ WALL_PROUD out, never
 *            above the host top, never inside another collider;
 *  - `top`   on the host's top face, inside its footprint, ≤ TOP_RISE high
 *            (lifting eyes, plank seams, hazard edging) — cover height reads
 *            true;
 *  - `floor` flat decals ≤ FLOOR_LIFT on the floor slab, under no collider
 *            (today only the painted-lines plane; the existing street pass
 *            already places drain grates);
 *  - `out`   scenery wholly OUTSIDE the perimeter walls' outer faces
 *            (grandstands, masts, scoreboard) — where no player plays.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import type { MapBox, MapDef } from "@neon-purge/shared";
import type { MapExtrasContext } from "../mapExtras.js";
import { normalMapFrom, surfaceMaps } from "../mapTextures.js";
import { dressable, minorKind } from "../mapDressing.js";

// ---------------------------------------------------------------------------
// contract numbers (the bench quotes these)
export const WALL_PROUD = 0.26;
export const TOP_RISE = 0.08;
export const FLOOR_LIFT = 0.05;
/** `out` pieces keep this clear of the perimeter walls' outer faces */
export const OUT_GAP = 0.02;

export type KMat = "galv" | "paint" | "hazard" | "lamp" | "ads" | "stand" | "wood" | "straw";
export type KZone = "skin" | "top" | "floor" | "out";
export type KShape = "box" | "rbox" | "cyl" | "eye";
export type V3 = [number, number, number];

export interface KPiece {
  mat: KMat;
  zone: KZone;
  shape: KShape;
  /** pivot (world) */
  o: V3;
  /** local size (cyl: [diameter, height, diameter] along local Y; eye: arch
   *  in the local XY plane, base at -sy/2) */
  s: V3;
  /** world matrix = T(o) · Ry(ry) · Rx(rx) · Rz(rz) · T(l) */
  ry?: number; rx?: number; rz?: number;
  l?: V3;
  color: number;
  cell?: Cell;
  /** cylinder top radius factor */
  taper?: number;
  /** host collider index (-1 for `out`) */
  host: number;
}

export interface FloorPaint {
  x0: number; z0: number; x1: number; z1: number; y: number;
  prims: Prim[];
}
export type Prim =
  | { t: "line"; a: [number, number]; b: [number, number]; w: number; col: string; dash?: number }
  | { t: "circle"; c: [number, number]; r: number; w: number; col: string; a0?: number; a1?: number }
  | { t: "disc"; c: [number, number]; r: number; col: string }
  | { t: "hatch"; x0: number; z0: number; x1: number; z1: number; col: string; w: number; gap: number }
  | { t: "text"; c: [number, number]; size: number; s: string; col: string; rot: number };

export interface KitLayout { pieces: KPiece[]; floor: FloorPaint | null }

// ---------------------------------------------------------------------------
// atlas (2048² canvas) — pixel rects [x0, y0, x1, y1]
export const CELLS = {
  ad0: [0, 0, 1024, 205], ad1: [1024, 0, 2048, 205],
  ad2: [0, 205, 1024, 410], ad3: [1024, 205, 2048, 410],
  ad4: [0, 410, 1024, 615], ad5: [1024, 410, 2048, 615],
  score: [0, 615, 1024, 1070],
  secA: [1024, 615, 1280, 871], secB: [1280, 615, 1536, 871], secC: [1536, 615, 1792, 871], secD: [1792, 615, 2048, 871],
  target0: [1024, 871, 1280, 1127], target1: [1280, 871, 1536, 1127], target2: [1536, 871, 1792, 1127],
  target3: [1792, 871, 2048, 1127], target4: [1024, 1127, 1280, 1383], target5: [1280, 1127, 1536, 1383],
  banner: [0, 1070, 1024, 1224],
  sign0: [0, 1224, 768, 1480], sign1: [0, 1480, 768, 1736], sign2: [768, 1480, 1536, 1736],
  fieldBanner: [0, 1736, 1024, 1890],
  // a solid colour patch (painted goal board etc. use vertex colour instead)
  plain: [1600, 1500, 1700, 1600],
} as const;
export type Cell = keyof typeof CELLS;
const AD_CELLS: Cell[] = ["ad0", "ad1", "ad2", "ad3", "ad4", "ad5"];
const SEC_CELLS: Cell[] = ["secA", "secB", "secC", "secD"];

// ---------------------------------------------------------------------------
// pure helpers

/** deterministic hash in [0,1) */
export function h01(a: number, b: number, salt: number): number {
  let x = Math.imul((Math.round(a * 97) * 73856093) ^ (Math.round(b * 89) * 19349663) ^ (salt * 83492791), 0x9e3779b1);
  x ^= x >>> 15; x = Math.imul(x, 0x85ebca6b); x ^= x >>> 13;
  return ((x >>> 0) % 100000) / 100000;
}

export interface AABB { x0: number; y0: number; z0: number; x1: number; y1: number; z1: number }
export const boxAABB = (b: MapBox): AABB => ({
  x0: b.p[0] - b.s[0] / 2, x1: b.p[0] + b.s[0] / 2,
  y0: b.p[1] - b.s[1] / 2, y1: b.p[1] + b.s[1] / 2,
  z0: b.p[2] - b.s[2] / 2, z1: b.p[2] + b.s[2] / 2,
});
export const overlaps = (a: AABB, b: AABB, e = 0): boolean =>
  a.x0 < b.x1 - e && a.x1 > b.x0 + e && a.y0 < b.y1 - e && a.y1 > b.y0 + e && a.z0 < b.z1 - e && a.z1 > b.z0 + e;

/** rotate a local point by Ry·Rx·Rz (the three.js Euler order "YXZ") */
function rot(p: KPiece, x: number, y: number, z: number): V3 {
  const rz = p.rz ?? 0, rx = p.rx ?? 0, ry = p.ry ?? 0;
  if (rz) { const c = Math.cos(rz), s = Math.sin(rz); const nx = x * c - y * s; y = x * s + y * c; x = nx; }
  if (rx) { const c = Math.cos(rx), s = Math.sin(rx); const ny = y * c - z * s; z = y * s + z * c; y = ny; }
  if (ry) { const c = Math.cos(ry), s = Math.sin(ry); const nx = x * c + z * s; z = -x * s + z * c; x = nx; }
  return [x, y, z];
}
/** world-space AABB of a piece's local box (conservative for cyl / eye) */
export function pieceAABB(p: KPiece): AABB {
  const l = p.l ?? [0, 0, 0];
  const r: AABB = { x0: Infinity, y0: Infinity, z0: Infinity, x1: -Infinity, y1: -Infinity, z1: -Infinity };
  for (let i = 0; i < 8; i++) {
    const v = rot(p, l[0] + ((i & 1) ? 0.5 : -0.5) * p.s[0], l[1] + ((i & 2) ? 0.5 : -0.5) * p.s[1], l[2] + ((i & 4) ? 0.5 : -0.5) * p.s[2]);
    const x = v[0] + p.o[0], y = v[1] + p.o[1], z = v[2] + p.o[2];
    r.x0 = Math.min(r.x0, x); r.x1 = Math.max(r.x1, x);
    r.y0 = Math.min(r.y0, y); r.y1 = Math.max(r.y1, y);
    r.z0 = Math.min(r.z0, z); r.z1 = Math.max(r.z1, z);
  }
  return r;
}

/** A side face of a box. `n` is the outward normal on its axis. */
interface Face {
  host: number; axis: "x" | "z"; plane: number; sign: 1 | -1;
  t0: number; t1: number; y0: number; y1: number;
}
function faces(i: number, a: AABB): Face[] {
  return [
    { host: i, axis: "x", plane: a.x1, sign: 1, t0: a.z0, t1: a.z1, y0: a.y0, y1: a.y1 },
    { host: i, axis: "x", plane: a.x0, sign: -1, t0: a.z0, t1: a.z1, y0: a.y0, y1: a.y1 },
    { host: i, axis: "z", plane: a.z1, sign: 1, t0: a.x0, t1: a.x1, y0: a.y0, y1: a.y1 },
    { host: i, axis: "z", plane: a.z0, sign: -1, t0: a.x0, t1: a.x1, y0: a.y0, y1: a.y1 },
  ];
}
/** yaw that turns local +z to the face's outward normal; local +x then runs
 *  along the face, left-to-right for a viewer looking at it */
const faceYaw = (f: Face): number => (f.axis === "x" ? f.sign * Math.PI / 2 : f.sign > 0 ? 0 : Math.PI);
/** world point on the face plane at tangent t, height y, `off` outward */
function facePoint(f: Face, t: number, y: number, off: number): V3 {
  return f.axis === "x" ? [f.plane + f.sign * off, y, t] : [t, y, f.plane + f.sign * off];
}

class Out {
  pieces: KPiece[] = [];
  constructor(private readonly A: AABB[], private readonly skip: Set<number>) {}
  /** is this AABB clear of every collider except `host` (and the floors)? */
  clear(a: AABB, host: number, e = 0.012): boolean {
    for (let j = 0; j < this.A.length; j++) {
      if (j === host || this.skip.has(j)) continue;
      if (overlaps(a, this.A[j], e)) return false;
    }
    return true;
  }
  /** add unless it collides; returns whether it was added */
  add(p: KPiece, probe?: AABB): boolean {
    if (!this.clear(probe ?? pieceAABB(p), p.host)) return false;
    this.pieces.push(p);
    return true;
  }
  /** a rectangle lying ON face `f`: t-range, y-range, `proud` thick */
  facePanel(f: Face, t0: number, t1: number, y0: number, y1: number, proud: number, mat: KMat, color: number,
    opts: { cell?: Cell; shape?: KShape; probeOut?: number; zone?: KZone } = {}): boolean {
    const tc = (t0 + t1) / 2, yc = (y0 + y1) / 2;
    const p: KPiece = {
      mat, zone: opts.zone ?? "skin", shape: opts.shape ?? "box",
      o: facePoint(f, tc, yc, proud / 2), s: [Math.abs(t1 - t0), y1 - y0, proud],
      ry: faceYaw(f), color, host: f.host, cell: opts.cell,
    };
    let probe: AABB | undefined;
    if (opts.probeOut) {
      const a = pieceAABB(p);
      probe = { ...a };
      if (f.axis === "x") { if (f.sign > 0) probe.x1 += opts.probeOut; else probe.x0 -= opts.probeOut; }
      else { if (f.sign > 0) probe.z1 += opts.probeOut; else probe.z0 -= opts.probeOut; }
    }
    return this.add(p, probe);
  }
  /** facePanel split into ≤ `seg`-long pieces, each kept only where clear */
  faceStrip(f: Face, t0: number, t1: number, y0: number, y1: number, proud: number, mat: KMat, color: number, seg = 1.0): void {
    const n = Math.max(1, Math.ceil((t1 - t0) / seg - 1e-6));
    const d = (t1 - t0) / n;
    for (let k = 0; k < n; k++) this.facePanel(f, t0 + k * d, t0 + (k + 1) * d, y0, y1, proud, mat, color);
  }
  /** flat strip on a horizontal surface at height y (x/z rect) */
  flat(x0: number, z0: number, x1: number, z1: number, y: number, th: number, mat: KMat, color: number, zone: KZone, host: number): boolean {
    return this.add({ mat, zone, shape: "box", o: [(x0 + x1) / 2, y + th / 2, (z0 + z1) / 2], s: [x1 - x0, th, z1 - z0], color, host });
  }
  /** an axis-aligned box given by its world extents */
  box(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, mat: KMat, color: number, zone: KZone, host: number, shape: KShape = "box"): boolean {
    return this.add({ mat, zone, shape, o: [(x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2], s: [x1 - x0, y1 - y0, z1 - z0], color, host });
  }
}

const PAL = {
  seam: 0x45474a, guard: 0xaeb3b8, eye: 0x6c6f72, lens: 0xfff3d6,
  mast: 0x9aa0a6, housing: 0x3a3e44, stand: 0xb8b3a9, standDark: 0x8e8a82,
  nosing: 0xd8a823, white: 0xeceae2, boardBack: 0x26292d,
  wood: 0xa7814f, woodDark: 0x7a5a36, log: 0x8a6a45, straw: 0xffffff,
  goal: 0x2f5a3a,
};
const SEAT_COLS: Record<string, number[]> = {
  arena: [0x1f5fa8, 0x1f5fa8, 0x1f5fa8, 0xd8dde2],
  field: [0x2f7a3e, 0x2f7a3e, 0xe6e2d4, 0x2f7a3e],
};

// ---------------------------------------------------------------------------
// map analysis

interface Wall { i: number; f: Face; out: Face; top: number }
interface Site {
  boxes: MapBox[]; A: AABB[];
  floors: Set<number>; floorTop: number;
  walls: Wall[];
  /** inner + outer rectangles of the perimeter */
  inner: { x0: number; z0: number; x1: number; z1: number };
  outer: { x0: number; z0: number; x1: number; z1: number };
  wallTop: number;
}
const NEAR = 75; // the real field: sandlot's mirror copy sits 200 m north

function analyse(map: MapDef): Site | null {
  const boxes = map.boxes;
  const A = boxes.map(boxAABB);
  let fi = -1, area = 0;
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i];
    if (Math.abs(A[i].y1) > 0.05 || b.s[1] > 1.5 || Math.hypot(b.p[0], b.p[2]) > 5) continue;
    if (b.s[0] * b.s[2] > area) { area = b.s[0] * b.s[2]; fi = i; }
  }
  if (fi < 0) return null;
  const floors = new Set<number>();
  for (let i = 0; i < boxes.length; i++) {
    const a = A[i];
    if (Math.abs(a.y1) <= 0.05 && boxes[i].s[1] <= 1.5 && a.x0 >= A[fi].x0 - 0.01 && a.x1 <= A[fi].x1 + 0.01 && a.z0 >= A[fi].z0 - 0.01 && a.z1 <= A[fi].z1 + 0.01) floors.add(i);
  }
  const floorTop = A[fi].y1;
  const walls: Wall[] = [];
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i], a = A[i];
    if (Math.abs(b.p[0]) > NEAR || Math.abs(b.p[2]) > NEAR) continue;
    if (b.s[1] < 5 || Math.max(b.s[0], b.s[2]) < 30 || Math.min(b.s[0], b.s[2]) > 1.2) continue;
    const alongX = b.s[0] > b.s[2];
    const fs = faces(i, a);
    if (alongX) {
      const inSign = b.p[2] > 0 ? -1 : 1;
      walls.push({ i, f: inSign < 0 ? fs[3] : fs[2], out: inSign < 0 ? fs[2] : fs[3], top: a.y1 });
    } else {
      const inSign = b.p[0] > 0 ? -1 : 1;
      walls.push({ i, f: inSign < 0 ? fs[1] : fs[0], out: inSign < 0 ? fs[0] : fs[1], top: a.y1 });
    }
  }
  if (walls.length !== 4) return null;
  const inner = { x0: -Infinity, z0: -Infinity, x1: Infinity, z1: Infinity };
  const outer = { ...inner };
  for (const w of walls) {
    if (w.f.axis === "x") {
      if (w.f.sign > 0) { inner.x0 = w.f.plane; outer.x0 = w.out.plane; } else { inner.x1 = w.f.plane; outer.x1 = w.out.plane; }
    } else if (w.f.sign > 0) { inner.z0 = w.f.plane; outer.z0 = w.out.plane; } else { inner.z1 = w.f.plane; outer.z1 = w.out.plane; }
  }
  const wallTop = Math.min(...walls.map((w) => w.top));
  return { boxes, A, floors, floorTop, walls, inner, outer, wallTop };
}

/** Does the renderer's minor-wall pass (mapDressing.dressMinor) already give
 *  this box a stone coping + footing? Same gate as renderer.ts. Those boxes
 *  are left alone: a container skin or hay bales under a stone cap would be
 *  two identities fighting, and the cap already dresses them. */
export function minorDressed(b: MapBox): boolean {
  if (b.glow || b.tag) return false;
  const [w, h, d] = b.s;
  return !dressable(w, h, d) && minorKind(w, h, d) !== null;
}

/** sits on the floor, inside the perimeter, not a wall / floor / mover */
function standing(site: Site, i: number): boolean {
  const b = site.boxes[i], a = site.A[i];
  if (site.floors.has(i) || site.walls.some((w) => w.i === i) || b.tag === "practice_mover") return false;
  if (Math.abs(a.y0 - site.floorTop) > 0.06) return false;
  return a.x0 >= site.inner.x0 - 0.01 && a.x1 <= site.inner.x1 + 0.01 && a.z0 >= site.inner.z0 - 0.01 && a.z1 <= site.inner.z1 + 0.01;
}

// ---------------------------------------------------------------------------
// shared dressing recipes

/** Precast-barrier treatment: stacked-block seams, hazard foot, corner guards,
 *  lifting eyes. */
function precast(o: Out, site: Site, i: number, lowSpec: boolean, eyes: boolean): void {
  const a = site.A[i];
  const h = a.y1 - a.y0;
  const fs = faces(i, a);
  const foot = a.y0 + 0.3;
  for (const f of fs) {
    const L = f.t1 - f.t0;
    // hazard-striped foot
    o.faceStrip(f, f.t0, f.t1, a.y0 + 0.004, foot, 0.01, "hazard", 0xffffff, 1.0);
    // stacked-block courses
    if (!lowSpec) {
      const n = Math.max(1, Math.round(h / 0.85));
      const ch = h / n;
      for (let k = 1; k < n; k++) {
        const y = a.y0 + k * ch;
        o.faceStrip(f, f.t0, f.t1, y - 0.018, y + 0.018, 0.006, "paint", PAL.seam, 1.0);
      }
      const segs = Math.max(1, Math.round(L / 1.8));
      if (segs >= 2) {
        const sl = L / segs;
        for (let k = 0; k < n; k++) {
          const y0 = Math.max(a.y0 + k * ch, foot), y1 = a.y0 + (k + 1) * ch;
          if (y1 - y0 < 0.1) continue;
          const shift = k % 2 ? sl / 2 : 0;
          for (let j = 1; j < segs + (shift ? 1 : 0); j++) {
            const t = f.t0 + j * sl - shift;
            if (t < f.t0 + 0.2 || t > f.t1 - 0.2) continue;
            o.facePanel(f, t - 0.016, t + 0.016, y0, y1, 0.006, "paint", PAL.seam);
          }
        }
      }
    }
    // galvanised corner angles on both ends of this face
    const gw = 0.1;
    o.facePanel(f, f.t0, f.t0 + gw, foot, a.y1, 0.014, "galv", PAL.guard);
    o.facePanel(f, f.t1 - gw, f.t1, foot, a.y1, 0.014, "galv", PAL.guard);
  }
  // lifting eyes on top, along the long axis
  if (eyes && !lowSpec && a.y1 - site.floorTop < 4.1) {
    const alongX = a.x1 - a.x0 >= a.z1 - a.z0;
    const L = alongX ? a.x1 - a.x0 : a.z1 - a.z0;
    const n = Math.max(2, Math.round(L / 2.4));
    for (let k = 0; k < n; k++) {
      const t = (k + 0.5) / n;
      const x = alongX ? a.x0 + t * L : (a.x0 + a.x1) / 2;
      const z = alongX ? (a.z0 + a.z1) / 2 : a.z0 + t * L;
      o.add({ mat: "galv", zone: "top", shape: "eye", o: [x, a.y1 + 0.03, z], s: [0.15, 0.07, 0.024], ry: alongX ? 0 : Math.PI / 2, color: PAL.eye, host: i });
    }
  }
}

/** hazard edge band round a walkable top */
function topEdge(o: Out, a: AABB, i: number, w: number, mat: KMat, color: number): void {
  const y = a.y1;
  o.flat(a.x0, a.z0, a.x1, a.z0 + w, y, 0.006, mat, color, "top", i);
  o.flat(a.x0, a.z1 - w, a.x1, a.z1, y, 0.006, mat, color, "top", i);
  o.flat(a.x0, a.z0 + w, a.x0 + w, a.z1 - w, y, 0.006, mat, color, "top", i);
  o.flat(a.x1 - w, a.z0 + w, a.x1, a.z1 - w, y, 0.006, mat, color, "top", i);
}

/** grandstand behind wall `w` between tangent t0..t1 (OUT zone) */
function grandstand(o: Out, site: Site, w: Wall, t0: number, t1: number, rows: number, seats: number[], lowSpec: boolean): void {
  const f = w.out; // outward face of the wall; +sign = away from the field
  const s = f.sign;
  const base = f.plane;
  // world box from (t, a=distance out, y) ranges
  const put = (ta: number, tb: number, a0: number, a1: number, y0: number, y1: number, mat: KMat, col: number): void => {
    const p0 = base + s * (OUT_GAP + a0), p1 = base + s * (OUT_GAP + a1);
    const lo = Math.min(p0, p1), hi = Math.max(p0, p1);
    if (f.axis === "x") o.box(lo, y0, ta, hi, y1, tb, mat, col, "out", -1);
    else o.box(ta, y0, lo, tb, y1, hi, mat, col, "out", -1);
  };
  const y0 = w.top + 0.35, tread = 0.9, rise = 0.46;
  const back = 0.45 + rows * tread;
  const topLast = y0 + (rows - 1) * rise;
  const roofY = topLast + 3.1;
  // front parapet (what the field sees first) + coping
  put(t0, t1, 0, 0.42, w.top - 1.2, y0 + 0.55, "stand", PAL.stand);
  put(t0, t1, 0, 0.46, y0 + 0.55, y0 + 0.62, "galv", PAL.guard);
  for (let r = 0; r < rows; r++) {
    const a0 = 0.45 + r * tread, top = y0 + r * rise;
    put(t0, t1, a0, a0 + tread, top - 1.3, top, "stand", r % 2 ? PAL.stand : PAL.standDark);
    if (lowSpec) {
      put(t0, t1, a0 + 0.3, a0 + 0.7, top + 0.36, top + 0.44, "paint", seats[0]);
      put(t0, t1, a0 + 0.66, a0 + 0.72, top + 0.44, top + 0.8, "paint", seats[0]);
      continue;
    }
    // tip-up seats, aisles every 14
    const pitch = 0.5;
    const n = Math.floor((t1 - t0 - 0.4) / pitch);
    for (let k = 0; k < n; k++) {
      if (k % 15 === 14) continue; // aisle
      const tc = t0 + 0.2 + (k + 0.5) * pitch;
      const sec = Math.floor(k / 15);
      let col = seats[(sec + r) % seats.length];
      if (h01(tc, r, 31) < 0.05) col = 0x8a8f94; // faded replacement seat
      put(tc - 0.21, tc + 0.21, a0 + 0.28, a0 + 0.68, top + 0.38, top + 0.45, "paint", col);
      put(tc - 0.21, tc + 0.21, a0 + 0.64, a0 + 0.7, top + 0.45, top + 0.84, "paint", col);
    }
    // aisle step nosings
    for (let k = 14; k < n; k += 15) {
      const tc = t0 + 0.2 + (k + 0.5) * pitch;
      put(tc - 0.3, tc + 0.3, a0, a0 + 0.06, top - 0.004, top + 0.004, "paint", PAL.nosing);
    }
  }
  // back wall, roof, roof beams, end cheeks
  put(t0, t1, back, back + 0.35, y0 - 1.3, roofY, "stand", PAL.stand);
  put(t0 - 0.3, t1 + 0.3, 0.8, back + 0.5, roofY, roofY + 0.24, "galv", PAL.mast);
  if (!lowSpec) {
    for (let t = t0 + 2; t < t1 - 1; t += 6) put(t - 0.12, t + 0.12, 1.0, back, roofY - 0.42, roofY, "galv", PAL.housing);
    // roof-edge lights
    for (let t = t0 + 3; t < t1 - 2; t += 6) put(t - 0.4, t + 0.4, 1.0, 1.25, roofY - 0.08, roofY - 0.02, "lamp", PAL.lens);
  }
  put(t0 - 0.35, t0, 0, back + 0.35, w.top - 1.2, roofY + 0.24, "stand", PAL.standDark);
  put(t1, t1 + 0.35, 0, back + 0.35, w.top - 1.2, roofY + 0.24, "stand", PAL.standDark);
}

/** floodlight mast at (x, z), lamps aimed at (cx, cz) */
function mast(o: Out, x: number, z: number, H: number, cx: number, cz: number, baseY: number, lowSpec: boolean): void {
  const ry = Math.atan2(cx - x, cz - z);
  o.add({ mat: "galv", zone: "out", shape: "cyl", o: [x, baseY + H / 2, z], s: [0.6, H, 0.6], taper: 0.55, color: PAL.mast, host: -1 });
  const hy = baseY + H + 1.3;
  const head = { o: [x, hy, z] as V3, ry, rx: 0.42 };
  o.add({ mat: "galv", zone: "out", shape: "box", ...head, s: [4.6, 2.8, 0.18], l: [0, 0, -0.1], color: PAL.housing, host: -1 });
  o.add({ mat: "galv", zone: "out", shape: "box", ...head, s: [0.5, 1.6, 0.5], l: [0, -1.7, -0.25], color: PAL.mast, host: -1 });
  o.add({ mat: "galv", zone: "out", shape: "box", ...head, s: [4.8, 0.08, 0.9], l: [0, -1.55, 0.35], color: PAL.mast, host: -1 });
  const cols = lowSpec ? 3 : 4, rowsN = lowSpec ? 2 : 3;
  for (let i = 0; i < cols; i++) for (let j = 0; j < rowsN; j++) {
    const lx = (i - (cols - 1) / 2) * 1.1, ly = (j - (rowsN - 1) / 2) * 0.88;
    o.add({ mat: "galv", zone: "out", shape: "box", ...head, s: [0.98, 0.76, 0.34], l: [lx, ly, 0.1], color: PAL.housing, host: -1 });
    o.add({ mat: "lamp", zone: "out", shape: "box", ...head, s: [0.84, 0.62, 0.04], l: [lx, ly, 0.29], color: PAL.lens, host: -1 });
  }
}

/** stadium scoreboard behind wall `w` centred at tangent tc (OUT zone) */
function scoreboard(o: Out, site: Site, w: Wall, tc: number): void {
  const f = w.out, s = f.sign;
  const a = OUT_GAP + 3.2;
  const p = f.plane + s * a;
  const yb = w.top + 2.2, W = 9.4, H = 4.6;
  const at = (t: number, off: number): V3 => (f.axis === "x" ? [p + s * off, 0, t] : [t, 0, p + s * off]);
  // faces the field: local +z = -outward normal
  const ry = f.axis === "x" ? -s * Math.PI / 2 : s > 0 ? Math.PI : 0;
  for (const dt of [-3.4, 3.4]) {
    const q = at(tc + dt, 0.2);
    o.add({ mat: "galv", zone: "out", shape: "cyl", o: [q[0], (site.floorTop - 3 + yb) / 2, q[2]], s: [0.5, yb - site.floorTop + 3, 0.5], color: PAL.mast, host: -1 });
  }
  const c = at(tc, 0);
  o.add({ mat: "galv", zone: "out", shape: "box", o: [c[0], yb + H / 2, c[2]], s: [W, H, 0.7], ry, color: PAL.boardBack, host: -1 });
  const fc = at(tc, -0.36);
  o.add({ mat: "ads", zone: "out", shape: "box", o: [fc[0], yb + H / 2, fc[2]], s: [W - 0.5, H - 0.5, 0.03], ry, color: 0xffffff, cell: "score", host: -1 });
  o.add({ mat: "galv", zone: "out", shape: "box", o: [c[0], yb + H + 0.12, c[2]], s: [W + 0.3, 0.24, 0.9], ry, color: PAL.mast, host: -1 });
}

/** perimeter-wall face dressing: LED ad boards, sector signs, panel joints,
 *  wall packs */
function perimeterFace(o: Out, site: Site, w: Wall, k: number, opts: { ads: boolean; lamps: boolean; signTs: number[]; bannerTs: number[]; lowSpec: boolean }): void {
  const f = w.f;
  const base = site.floorTop;
  if (opts.ads) {
    const seg = 4.6, gap = 0.3;
    let j = 0;
    for (let t = f.t0 + 0.6; t + seg <= f.t1 - 0.6; t += seg + gap, j++) {
      const ta = t, tb = t + seg;
      if (o.facePanel(f, ta, tb, base + 0.12, base + 1.12, 0.05, "galv", PAL.boardBack, { probeOut: 0.6 })) {
        o.facePanel(f, ta + 0.05, tb - 0.05, base + 0.17, base + 1.07, 0.075, "ads", 0xffffff, { cell: AD_CELLS[(j + k * 2) % AD_CELLS.length] });
      }
    }
  }
  for (const tc of opts.signTs) {
    o.facePanel(f, tc - 1.15, tc + 1.15, base + 2.4, base + 4.7, 0.03, "ads", 0xffffff, { cell: SEC_CELLS[k % 4], probeOut: 0.3 });
  }
  for (const tc of opts.bannerTs) {
    o.facePanel(f, tc - 3.9, tc + 3.9, w.top - 2.1, w.top - 0.9, 0.03, "ads", 0xffffff, { cell: "banner", probeOut: 0.3 });
  }
  if (!opts.lowSpec) {
    // precast panel joints above the board line
    for (let t = f.t0 + 6; t < f.t1 - 1; t += 6) o.facePanel(f, t - 0.022, t + 0.022, base + 1.3, w.top - 0.05, 0.006, "paint", PAL.seam);
    o.faceStrip(f, f.t0, f.t1, base + 1.26, base + 1.3, 0.006, "paint", PAL.seam, 3);
  }
  if (opts.lamps) {
    for (let t = f.t0 + 4.5; t < f.t1 - 2; t += 9) {
      const y = w.top - 0.55;
      if (o.facePanel(f, t - 0.3, t + 0.3, y - 0.11, y + 0.11, 0.24, "galv", PAL.housing)) {
        o.facePanel(f, t - 0.24, t + 0.24, y - 0.13, y - 0.11, 0.2, "lamp", PAL.lens);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// ARENA (grid_arena, arena_xl)

export function arenaLayout(map: MapDef, lowSpec: boolean): KitLayout {
  const site = analyse(map);
  if (!site) return { pieces: [], floor: null };
  const { A, boxes } = site;
  const o = new Out(A, site.floors);
  const xl = site.inner.x1 - site.inner.x0 > 70;
  const cx = (site.inner.x0 + site.inner.x1) / 2, cz = (site.inner.z0 + site.inner.z1) / 2;

  // -- classify the standing boxes
  const platforms: number[] = [], stairs: number[] = [], cover: number[] = [];
  for (let i = 0; i < boxes.length; i++) {
    if (!standing(site, i)) continue;
    const b = boxes[i];
    const mn = Math.min(b.s[0], b.s[2]), mx = Math.max(b.s[0], b.s[2]);
    if (Math.abs(b.s[1] - 2) < 0.06 && mn >= 5) platforms.push(i);
    else if (b.s[1] <= 1.55 && mn <= 1.3 && mx >= 4) stairs.push(i);
    else if (b.s[1] <= 6) cover.push(i);
  }
  for (const i of cover) precast(o, site, i, lowSpec, true);
  for (const i of platforms) {
    precast(o, site, i, lowSpec, false);
    topEdge(o, A[i], i, 0.18, "hazard", 0xffffff);
  }
  for (const i of stairs) {
    if (minorDressed(boxes[i])) continue; // the stone coping band owns that edge
    // yellow nosing on the step's front (the edge facing away from the platform)
    const a = A[i];
    const mid = (a.x0 + a.x1) / 2, midZ = (a.z0 + a.z1) / 2;
    let pc = 0, best = Infinity;
    for (const j of platforms) {
      const p = A[j], d = Math.hypot((p.x0 + p.x1) / 2 - mid, (p.z0 + p.z1) / 2 - midZ);
      if (d < best) { best = d; pc = (p.x0 + p.x1) / 2; }
    }
    const xFront = mid < pc ? a.x0 : a.x1 - 0.08;
    o.flat(xFront, a.z0, xFront + 0.08, a.z1, a.y1, 0.005, "paint", PAL.nosing, "top", i);
  }

  // -- perimeter walls (inner faces)
  const cells = xl ? [-25, 25] : [0];
  site.walls.forEach((w, k) => {
    const f = w.f;
    const tMid = (f.t0 + f.t1) / 2;
    perimeterFace(o, site, w, k, {
      ads: true, lamps: true, lowSpec,
      signTs: cells.map((c) => tMid + c),
      bannerTs: xl ? [tMid - 12, tMid + 12] : [tMid - 12, tMid + 12],
    });
  });

  // -- outside: grandstands on the x walls, scoreboards on the z walls, masts
  const seats = SEAT_COLS.arena;
  for (const w of site.walls) {
    const f = w.out, tMid = (f.t0 + f.t1) / 2, half = (f.t1 - f.t0) / 2;
    if (f.axis === "x") grandstand(o, site, w, tMid - half * 0.68, tMid + half * 0.68, lowSpec ? 6 : 8, seats, lowSpec);
    else if (f.sign < 0 || xl) scoreboard(o, site, w, tMid);
  }
  const H = xl ? 24 : 17, m = 3.2;
  for (const [mx, mz] of [[site.outer.x0 - m, site.outer.z0 - m], [site.outer.x1 + m, site.outer.z0 - m], [site.outer.x0 - m, site.outer.z1 + m], [site.outer.x1 + m, site.outer.z1 + m]]) {
    mast(o, mx, mz, H, cx, cz, site.floorTop - 3, lowSpec);
  }
  if (xl) for (const sx of [-1, 1]) for (const z of [site.outer.z0 - m, site.outer.z1 + m]) mast(o, cx + sx * 30, z, H, cx + sx * 25, cz, site.floorTop - 3, lowSpec);

  // -- floor paint
  const prims: Prim[] = [];
  const W = "#eeeae0", Y = "#e2b52b";
  const I = site.inner, ins = 0.9;
  const rect = (x0: number, z0: number, x1: number, z1: number, w: number, col: string): void => {
    prims.push({ t: "line", a: [x0, z0], b: [x1, z0], w, col }, { t: "line", a: [x1, z0], b: [x1, z1], w, col },
      { t: "line", a: [x1, z1], b: [x0, z1], w, col }, { t: "line", a: [x0, z1], b: [x0, z0], w, col });
  };
  rect(I.x0 + ins, I.z0 + ins, I.x1 - ins, I.z1 - ins, 0.12, W);
  const centres: [number, number][] = xl ? [[-25, -25], [25, -25], [-25, 25], [25, 25], [0, 0]] : [[0, 0]];
  for (const [x, z] of centres) {
    prims.push({ t: "circle", c: [x, z], r: 4.6, w: 0.12, col: W });
    prims.push({ t: "circle", c: [x, z], r: 5.1, w: 0.05, col: W });
  }
  if (xl) {
    for (const [a, b] of [[[0, -7], [0, I.z0 + 1.5]], [[0, 7], [0, I.z1 - 1.5]], [[-7, 0], [I.x0 + 1.5, 0]], [[7, 0], [I.x1 - 1.5, 0]]] as [number, number][][]) {
      prims.push({ t: "line", a, b, w: 0.14, col: Y, dash: 1.6 });
    }
  }
  // corner spawn zones: hatched yellow squares with a number
  const zs = 5.2, zi = 1.5;
  const corners: [number, number, number, number][] = [
    [I.x0 + zi, I.z0 + zi, 1, 1], [I.x1 - zi, I.z0 + zi, -1, 1], [I.x0 + zi, I.z1 - zi, 1, -1], [I.x1 - zi, I.z1 - zi, -1, -1],
  ];
  corners.forEach(([x, z, sx, sz], k) => {
    const x0 = Math.min(x, x + sx * zs), x1 = Math.max(x, x + sx * zs), z0 = Math.min(z, z + sz * zs), z1 = Math.max(z, z + sz * zs);
    prims.push({ t: "hatch", x0, z0, x1, z1, col: Y, w: 0.12, gap: 0.6 });
    rect(x0, z0, x1, z1, 0.14, Y);
    prims.push({ t: "text", c: [x + sx * zs / 2, z + sz * zs / 2], size: 2.2, s: String(k + 1), col: W, rot: Math.atan2(cx - x, -(cz - z)) });
  });
  const floor: FloorPaint = { x0: I.x0, z0: I.z0, x1: I.x1, z1: I.z1, y: site.floorTop + 0.012, prims };
  return { pieces: o.pieces, floor };
}

// ---------------------------------------------------------------------------
// PRACTICE FIELD (sandlot)

const near = (a: string | undefined, b: string): boolean => (a ?? "").toLowerCase() === b;

export function fieldLayout(map: MapDef, lowSpec: boolean): KitLayout {
  const site = analyse(map);
  if (!site) return { pieces: [], floor: null };
  const { A, boxes } = site;
  const o = new Out(A, site.floors);
  const cx = (site.inner.x0 + site.inner.x1) / 2, cz = (site.inner.z0 + site.inner.z1) / 2;
  const yTop = site.floorTop;

  const forts: number[] = [];
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i], a = A[i];
    const inField = a.x0 >= site.inner.x0 - 0.01 && a.x1 <= site.inner.x1 + 0.01 && a.z0 >= site.inner.z0 - 0.01 && a.z1 <= site.inner.z1 + 0.01;
    if (!inField || site.floors.has(i) || site.walls.some((w) => w.i === i) || b.tag === "practice_mover") continue;
    const col = b.color;
    const onFloor = Math.abs(a.y0 - yTop) < 0.06;
    const fs = faces(i, a);
    if ((near(col, "#c9a36b") || near(col, "#a8814d")) && onFloor && b.s[0] >= 8 && b.s[2] >= 8) {
      forts.push(i);
      continue;
    }
    if (minorDressed(b)) continue;
    if (near(col, "#c9a36b") || near(col, "#a8814d")) {
      crate(o, i, a, fs, lowSpec, !onFloor || b.s[1] < 1.1 && Math.max(b.s[0], b.s[2]) > 2.5);
    } else if (near(col, "#7a7f88") && onFloor) {
      precast(o, site, i, lowSpec, true);
    }
  }
  for (const i of forts) fort(o, i, A[i], lowSpec);

  // perimeter: shooting targets + range signage on the long walls
  let lane = 0;
  for (const w of site.walls) {
    const f = w.f;
    if (f.axis !== "z") {
      // end walls behind the forts: range rules above deck height
      const tMid = (f.t0 + f.t1) / 2;
      for (const [t, cell] of [[-12, "sign2"], [12, "sign1"]] as [number, Cell][]) {
        o.facePanel(f, tMid + t - 1.2, tMid + t + 1.2, yTop + 2.75, yTop + 3.55, 0.035, "ads", 0xffffff, { cell, probeOut: 0.3 });
      }
      continue;
    }
    const north = f.sign > 0; // the -z wall faces +z
    for (const x of [-15, -11, -7, 7, 11, 15]) {
      const t = x;
      const y = yTop + 1.5;
      if (!o.facePanel(f, t - 0.62, t + 0.62, y - 0.62, y + 0.62, 0.16, "straw", PAL.straw, { shape: "rbox", probeOut: 0.5 })) continue;
      o.facePanel(f, t - 0.5, t + 0.5, y - 0.5, y + 0.5, 0.175, "ads", 0xffffff, { cell: (`target${lane % 6}`) as Cell });
      lane++;
      for (const dt of [-0.72, 0.72]) o.facePanel(f, t + dt - 0.05, t + dt + 0.05, yTop, y + 0.75, 0.22, "wood", PAL.woodDark);
      o.facePanel(f, t - 0.8, t + 0.8, y + 0.66, y + 0.76, 0.24, "wood", PAL.woodDark);
    }
    const tMid = (f.t0 + f.t1) / 2;
    const signs: [number, Cell][] = north ? [[-19.5 + 0.0, "sign0"], [19.5, "sign1"]] : [[-3.2, "sign2"], [3.2, "sign0"]];
    for (const [t, cell] of signs) o.facePanel(f, tMid + t - 1.2, tMid + t + 1.2, yTop + 2.75, yTop + 3.55, 0.035, "ads", 0xffffff, { cell, probeOut: 0.3 });
    if (!north) o.facePanel(f, tMid - 4, tMid + 4, yTop + 3.75, yTop + 4.95 - 0.8, 0.03, "ads", 0xffffff, { cell: "fieldBanner", probeOut: 0.3 });
  }

  // outside: grandstand behind the south wall, scoreboard north, four masts
  for (const w of site.walls) {
    const f = w.out, tMid = (f.t0 + f.t1) / 2;
    if (f.axis === "z" && f.sign > 0) grandstand(o, site, w, tMid - 18, tMid + 18, lowSpec ? 5 : 7, SEAT_COLS.field, lowSpec);
    else if (f.axis === "z") scoreboard(o, site, w, tMid);
  }
  const m = 3.2;
  for (const [mx, mz] of [[site.outer.x0 - m, site.outer.z0 - m], [site.outer.x1 + m, site.outer.z0 - m], [site.outer.x0 - m, site.outer.z1 + m], [site.outer.x1 + m, site.outer.z1 + m]]) {
    mast(o, mx, mz, 17, cx, cz, yTop - 3, lowSpec);
  }

  // chalk pitch between the forts
  let fx = 19;
  for (const i of forts) fx = Math.min(fx, Math.abs(A[i].x0) < Math.abs(A[i].x1) ? Math.abs(A[i].x0) : Math.abs(A[i].x1));
  const px = fx - 3.2, pz = site.inner.z1 - 1.4;
  const Wc = "#f2f1ea";
  const prims: Prim[] = [];
  const line = (a: [number, number], b: [number, number], w = 0.12): void => { prims.push({ t: "line", a, b, w, col: Wc }); };
  line([-px, -pz], [px, -pz]); line([px, -pz], [px, pz]); line([px, pz], [-px, pz]); line([-px, pz], [-px, -pz]);
  line([0, -pz], [0, pz]);
  prims.push({ t: "circle", c: [0, 0], r: 4.6, w: 0.12, col: Wc });
  for (const s of [-1, 1]) {
    const gx = s * px;
    line([gx, -7.5], [gx - s * 5, -7.5]); line([gx - s * 5, -7.5], [gx - s * 5, 7.5]); line([gx - s * 5, 7.5], [gx, 7.5]);
    line([gx, -3.4], [gx - s * 1.8, -3.4]); line([gx - s * 1.8, -3.4], [gx - s * 1.8, 3.4]); line([gx - s * 1.8, 3.4], [gx, 3.4]);
    prims.push({ t: "disc", c: [gx - s * 3.7, 0], r: 0.13, col: Wc });
    const a = Math.acos(1.3 / 3.0);
    prims.push({ t: "circle", c: [gx - s * 3.7, 0], r: 3.0, w: 0.12, col: Wc, a0: s > 0 ? Math.PI - a : -a, a1: s > 0 ? Math.PI + a : a });
    for (const zs of [-1, 1]) {
      const a0 = s > 0 ? (zs > 0 ? Math.PI : Math.PI / 2) : (zs > 0 ? -Math.PI / 2 : 0);
      prims.push({ t: "circle", c: [gx, zs * pz], r: 0.9, w: 0.1, col: Wc, a0: a0 + (zs < 0 ? 0 : 0), a1: a0 + Math.PI / 2 });
    }
  }
  const floor: FloorPaint = { x0: -px - 1, z0: site.inner.z0 + 0.1, x1: px + 1, z1: site.inner.z1 - 0.1, y: yTop + 0.022, prims };
  return { pieces: o.pieces, floor };
}

/** nailed wooden crate: edge boards + a diagonal brace on every face */
function crate(o: Out, i: number, a: AABB, fs: Face[], lowSpec: boolean, step: boolean): void {
  const b = 0.13, pr = 0.03;
  const col = h01(a.x0, a.z0, 3) < 0.5 ? PAL.wood : 0x9a7546;
  for (const f of fs) {
    const W = f.t1 - f.t0, H = f.y1 - f.y0;
    o.facePanel(f, f.t0, f.t1, f.y1 - b, f.y1, pr, "wood", col);
    o.facePanel(f, f.t0, f.t1, f.y0, f.y0 + b, pr, "wood", col);
    o.facePanel(f, f.t0, f.t0 + b, f.y0 + b, f.y1 - b, pr, "wood", col);
    o.facePanel(f, f.t1 - b, f.t1, f.y0 + b, f.y1 - b, pr, "wood", col);
    if (step || lowSpec || H < 0.8) continue;
    const iw = W - 2 * b, ih = H - 2 * b;
    const len = Math.hypot(iw, ih) - 0.1;
    const ang = Math.atan2(ih, iw) * (h01(f.plane, f.t0, 9) < 0.5 ? 1 : -1);
    o.add({ mat: "wood", zone: "skin", shape: "box", o: facePoint(f, (f.t0 + f.t1) / 2, (f.y0 + f.y1) / 2, pr / 2), s: [len, 0.11, pr], ry: faceYaw(f), rz: ang, color: PAL.woodDark, host: i });
  }
  // top: edge boards
  const y = a.y1, th = 0.012;
  o.flat(a.x0, a.z0, a.x1, a.z0 + b, y, th, "wood", col, "top", i);
  o.flat(a.x0, a.z1 - b, a.x1, a.z1, y, th, "wood", col, "top", i);
  o.flat(a.x0, a.z0 + b, a.x0 + b, a.z1 - b, y, th, "wood", col, "top", i);
  o.flat(a.x1 - b, a.z0 + b, a.x1, a.z1 - b, y, th, "wood", col, "top", i);
  if (!lowSpec) {
    // plank seams on the lid
    const alongX = a.x1 - a.x0 >= a.z1 - a.z0;
    const span = alongX ? a.z1 - a.z0 : a.x1 - a.x0;
    const n = Math.max(2, Math.round(span / 0.3));
    for (let k = 1; k < n; k++) {
      const t = (alongX ? a.z0 : a.x0) + (k * span) / n;
      if (alongX) o.flat(a.x0 + b, t - 0.012, a.x1 - b, t + 0.012, y, 0.004, "paint", 0x4a3622, "top", i);
      else o.flat(t - 0.012, a.z0 + b, t + 0.012, a.z1 - b, y, 0.004, "paint", 0x4a3622, "top", i);
    }
  }
}

/** fort platform: log palisade on the open sides, deck planks, a painted
 *  practice goal on the yard face */
function fort(o: Out, i: number, a: AABB, lowSpec: boolean): void {
  const fs = faces(i, a);
  const yard = Math.abs(a.x0) < Math.abs(a.x1) ? fs[1] : fs[0]; // face toward x = 0
  const step = lowSpec ? 0.34 : 0.27, d = 0.25;
  for (const f of fs) {
    const isYard = f === yard;
    for (let t = f.t0 + step / 2; t < f.t1 - step / 2 + 1e-6; t += step) {
      if (isYard && Math.abs(t - (f.t0 + f.t1) / 2) < 3.05) continue;
      const hgt = (f.y1 - f.y0) - 0.03 - h01(t, f.plane, 2) * 0.05;
      o.add({ mat: "wood", zone: "skin", shape: "cyl", o: facePoint(f, t, f.y0 + hgt / 2, 0.24 - d / 2), s: [d, hgt, d], color: shadeHex(PAL.log, 0.85 + h01(t, f.plane, 7) * 0.3), host: i });
    }
  }
  // goal board + white frame on the yard face
  const tc = (yard.t0 + yard.t1) / 2, gw = 2.8;
  if (o.facePanel(yard, tc - gw - 0.2, tc + gw + 0.2, a.y0, a.y1 - 0.02, 0.06, "wood", PAL.wood)) {
    o.facePanel(yard, tc - gw + 0.12, tc + gw - 0.12, a.y0 + 0.05, a.y1 - 0.2, 0.065, "paint", PAL.goal);
    o.facePanel(yard, tc - gw, tc - gw + 0.12, a.y0, a.y1 - 0.08, 0.1, "paint", PAL.white);
    o.facePanel(yard, tc + gw - 0.12, tc + gw, a.y0, a.y1 - 0.08, 0.1, "paint", PAL.white);
    o.facePanel(yard, tc - gw, tc + gw, a.y1 - 0.2, a.y1 - 0.08, 0.1, "paint", PAL.white);
    for (const u of [-1, 1]) o.facePanel(yard, tc + u * (gw - 0.72) - 0.4, tc + u * (gw - 0.72) + 0.4, a.y1 - 1.05, a.y1 - 0.25, 0.08, "ads", 0xffffff, { cell: u < 0 ? "target0" : "target3" });
  }
  // deck planks
  if (!lowSpec) {
    for (let z = a.z0 + 0.3; z < a.z1 - 0.1; z += 0.3) o.flat(a.x0 + 0.05, z - 0.012, a.x1 - 0.05, z + 0.012, a.y1, 0.004, "paint", 0x5e4429, "top", i);
  }
}

function shadeHex(hex: number, f: number): number {
  const c = (s: number): number => Math.min(255, Math.round(((hex >> s) & 255) * f));
  return (c(16) << 16) | (c(8) << 8) | c(0);
}

// ---------------------------------------------------------------------------
// THREE: textures + build (client only)

let atlasTex: THREE.CanvasTexture | null = null;
let hazardTex: THREE.CanvasTexture | null = null;
let strawTex: { map: THREE.CanvasTexture; normal: THREE.CanvasTexture } | null = null;

function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => { s = (s + 0x6d2b79f5) >>> 0; let t = s; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  return [c, c.getContext("2d")!];
}
function tex(c: HTMLCanvasElement, repeat: boolean): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  if (repeat) t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.userData.shared = true;
  return t;
}

function atlas(): THREE.CanvasTexture {
  if (atlasTex) return atlasTex;
  const [c, g] = canvas(2048, 2048);
  const R = CELLS;
  const box = (r: readonly number[], fill: string | CanvasGradient): void => { g.fillStyle = fill; g.fillRect(r[0], r[1], r[2] - r[0], r[3] - r[1]); };
  const txt = (r: readonly number[], s: string, font: string, fill: string, fx = 0.5, fy = 0.5, align: CanvasTextAlign = "center"): void => {
    g.fillStyle = fill; g.font = font; g.textAlign = align; g.textBaseline = "middle";
    g.fillText(s, r[0] + (r[2] - r[0]) * fx, r[1] + (r[3] - r[1]) * fy, (r[2] - r[0]) * 0.92);
  };
  const grad = (r: readonly number[], a: string, b: string): CanvasGradient => {
    const gr = g.createLinearGradient(r[0], r[1], r[0], r[3]); gr.addColorStop(0, a); gr.addColorStop(1, b); return gr;
  };
  // six fictional sponsors, LED-board style
  const ads: [Cell, string, string, string, string, string][] = [
    ["ad0", "#0d2a52", "#123a70", "VOLTA ENERGY", "#ffd23a", "POWER THE RUSH"],
    ["ad1", "#b3171d", "#d62a22", "HYDRA COLA", "#ffffff", "ICE COLD SINCE 1987"],
    ["ad2", "#111214", "#25272b", "KESTREL OPTICS", "#7fe0ff", "SEE FIRST"],
    ["ad3", "#f2f0ea", "#d9d6cc", "ORBIT TYRES", "#1b1b1b", "GRIP  ·  WET  ·  DRY"],
    ["ad4", "#0f5132", "#1a7a4a", "IRONCLAD GEAR", "#ffffff", "BUILT FOR THE FIELD"],
    ["ad5", "#1a1a1a", "#2c1640", "NEON PURGE LEAGUE", "#ff4fd8", "SEASON 7"],
  ];
  for (const [cell, a, b, name, fg, tag] of ads) {
    const r = R[cell];
    box(r, grad(r, a, b));
    txt(r, name, "900 112px Arial Black, Arial, sans-serif", fg, 0.5, 0.42);
    txt(r, tag, "bold 38px Arial, sans-serif", fg, 0.5, 0.82);
    // LED pixel grid
    g.fillStyle = "rgba(0,0,0,0.18)";
    for (let x = r[0]; x < r[2]; x += 6) g.fillRect(x, r[1], 1, r[3] - r[1]);
    for (let y = r[1]; y < r[3]; y += 6) g.fillRect(r[0], y, r[2] - r[0], 1);
  }
  // scoreboard
  {
    const r = R.score;
    box(r, "#0b0c0e");
    g.strokeStyle = "#3a3d42"; g.lineWidth = 10; g.strokeRect(r[0] + 8, r[1] + 8, r[2] - r[0] - 16, r[3] - r[1] - 16);
    txt(r, "NEON PURGE ARENA", "900 64px Arial Black, Arial, sans-serif", "#ffcf3a", 0.5, 0.14);
    txt(r, "HOME", "bold 50px Arial, sans-serif", "#e6e6e6", 0.2, 0.33);
    txt(r, "GUEST", "bold 50px Arial, sans-serif", "#e6e6e6", 0.8, 0.33);
    txt(r, "12", "bold 170px Courier New, monospace", "#ff4a2a", 0.2, 0.6);
    txt(r, "09", "bold 170px Courier New, monospace", "#ff4a2a", 0.8, 0.6);
    txt(r, "07:42", "bold 96px Courier New, monospace", "#ffd84a", 0.5, 0.55);
    txt(r, "ROUND 2", "bold 44px Arial, sans-serif", "#8fd0ff", 0.5, 0.88);
  }
  // sector letters (stencil on yellow)
  (["secA", "secB", "secC", "secD"] as Cell[]).forEach((cell, k) => {
    const r = R[cell];
    box(r, "#e2b52b");
    g.strokeStyle = "#141414"; g.lineWidth = 14; g.strokeRect(r[0] + 14, r[1] + 14, r[2] - r[0] - 28, r[3] - r[1] - 28);
    txt(r, "ABCD"[k], "900 190px Arial Black, Arial, sans-serif", "#141414", 0.5, 0.55);
  });
  // banner
  {
    const r = R.banner;
    box(r, grad(r, "#f4f2ec", "#d8d4ca"));
    box([r[0], r[1], r[0] + 150, r[3]], "#b8232a");
    txt(r, "NP", "900 84px Arial Black, Arial, sans-serif", "#ffffff", 0.073, 0.52);
    txt([r[0] + 150, r[1], r[2], r[3]], "NEON PURGE TRAINING CENTRE", "900 66px Arial Black, Arial, sans-serif", "#1c2733", 0.5, 0.52);
  }
  // range targets (paper, scoring rings, lane number)
  for (let k = 0; k < 6; k++) {
    const r = R[`target${k}` as Cell];
    const x = (r[0] + r[2]) / 2, y = (r[1] + r[3]) / 2;
    box(r, "#e9dfc4");
    for (let ring = 10; ring >= 1; ring--) {
      g.beginPath(); g.arc(x, y + 6, ring * 11.5, 0, Math.PI * 2);
      g.fillStyle = ring <= 6 ? "#151515" : "#e9dfc4"; g.fill();
      g.strokeStyle = ring <= 6 ? "#e9dfc4" : "#151515"; g.lineWidth = 2; g.stroke();
    }
    g.beginPath(); g.arc(x, y + 6, 5, 0, Math.PI * 2); g.fillStyle = "#e9dfc4"; g.fill();
    txt([r[0] + 8, r[1] + 4, r[0] + 70, r[1] + 44], String(k + 1), "bold 34px Arial, sans-serif", "#b8232a", 0.5, 0.5);
    // a few bullet holes
    const rr = rng(77 + k);
    g.fillStyle = "#2a2622";
    for (let j = 0; j < 7; j++) { g.beginPath(); g.arc(x + (rr() - 0.5) * 120, y + (rr() - 0.5) * 120, 3.5, 0, Math.PI * 2); g.fill(); }
  }
  // range signs
  const sign = (cell: Cell, head: string, headBg: string, body: string[]): void => {
    const r = R[cell];
    box(r, "#f4f2ec");
    box([r[0], r[1], r[2], r[1] + 80], headBg);
    txt([r[0], r[1], r[2], r[1] + 80], head, "900 58px Arial Black, Arial, sans-serif", "#ffffff");
    body.forEach((s, k) => txt([r[0], r[1] + 90 + k * 78, r[2], r[1] + 160 + k * 78], s, "bold 50px Arial, sans-serif", "#1b1b1b"));
    g.strokeStyle = "#1b1b1b"; g.lineWidth = 8; g.strokeRect(r[0] + 4, r[1] + 4, r[2] - r[0] - 8, r[3] - r[1] - 8);
  };
  sign("sign0", "CAUTION", "#d8a823", ["LIVE FIRE RANGE", "EYE & EAR PROTECTION"]);
  sign("sign1", "DANGER", "#b8232a", ["NO ENTRY WHEN", "RED FLAG IS FLYING"]);
  sign("sign2", "RANGE RULES", "#1f5fa8", ["CEASE FIRE ON WHISTLE", "MUZZLE DOWNRANGE"]);
  {
    const r = R.fieldBanner;
    box(r, grad(r, "#2f7a3e", "#1f5a2c"));
    txt(r, "PRACTICE FIELD  ·  EST. 2019", "900 64px Arial Black, Arial, sans-serif", "#f2f1ea", 0.5, 0.52);
  }
  box(R.plain, "#ffffff");
  // weathering over everything: faint grime speckle
  const rr = rng(5);
  g.fillStyle = "rgba(60,50,40,0.06)";
  for (let k = 0; k < 9000; k++) g.fillRect(rr() * 2048, rr() * 2048, 2 + rr() * 4, 2 + rr() * 4);
  atlasTex = tex(c, false);
  return atlasTex;
}

function hazard(): THREE.CanvasTexture {
  if (hazardTex) return hazardTex;
  const [c, g] = canvas(256, 256);
  g.fillStyle = "#dcae22"; g.fillRect(0, 0, 256, 256);
  g.fillStyle = "#1c1c1c";
  for (let k = -2; k < 4; k++) {
    const x = k * 128;
    g.beginPath(); g.moveTo(x, 256); g.lineTo(x + 64, 256); g.lineTo(x + 64 + 256, 0); g.lineTo(x + 256, 0); g.fill();
  }
  const r = rng(9);
  for (let k = 0; k < 1400; k++) {
    g.fillStyle = r() < 0.5 ? "rgba(120,118,110,0.35)" : "rgba(30,25,20,0.25)";
    g.fillRect(r() * 256, r() * 256, 1 + r() * 5, 1 + r() * 3);
  }
  hazardTex = tex(c, true);
  return hazardTex;
}

function straw(): { map: THREE.CanvasTexture; normal: THREE.CanvasTexture } {
  if (strawTex) return strawTex;
  const [c, g] = canvas(256, 256);
  g.fillStyle = "#c39a48"; g.fillRect(0, 0, 256, 256);
  const r = rng(21);
  g.lineCap = "round";
  for (let k = 0; k < 2600; k++) {
    const x = r() * 256, y = r() * 256, len = 6 + r() * 22, a = (r() - 0.5) * 0.9;
    const v = r();
    g.strokeStyle = v < 0.3 ? "rgba(120,88,35,0.8)" : v < 0.75 ? "rgba(222,190,110,0.85)" : "rgba(245,222,150,0.9)";
    g.lineWidth = 0.8 + r() * 1.6;
    g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len); g.stroke();
  }
  const map = tex(c, true);
  const normal = new THREE.CanvasTexture(normalMapFrom(c, 3.0));
  normal.wrapS = normal.wrapT = THREE.RepeatWrapping;
  normal.userData.shared = true;
  strawTex = { map, normal };
  return strawTex;
}

/** world-projected UVs (dominant normal axis), re-anchored so values stay small */
function projUV(g: THREE.BufferGeometry, tile: number, ax: number, ay: number, az: number): void {
  const pos = g.getAttribute("position"), nor = g.getAttribute("normal"), uv = g.getAttribute("uv");
  const ox = Math.floor(ax / tile) * tile, oy = Math.floor(ay / tile) * tile, oz = Math.floor(az / tile) * tile;
  for (let i = 0; i < pos.count; i++) {
    const nx = Math.abs(nor.getX(i)), ny = Math.abs(nor.getY(i)), nz = Math.abs(nor.getZ(i));
    const x = pos.getX(i) - ox, y = pos.getY(i) - oy, z = pos.getZ(i) - oz;
    let u: number, v: number;
    if (nx >= ny && nx >= nz) { u = z; v = y; } else if (ny >= nz) { u = x; v = z; } else { u = x; v = y; }
    uv.setXY(i, u / tile, v / tile);
  }
  uv.needsUpdate = true;
}

const tmpM = new THREE.Matrix4(), tmpE = new THREE.Euler(0, 0, 0, "YXZ"), tmpQ = new THREE.Quaternion(), tmpV = new THREE.Vector3(), ONE = new THREE.Vector3(1, 1, 1);
const tmpC = new THREE.Color();

function pieceGeo(p: KPiece, lowSpec: boolean): THREE.BufferGeometry {
  let g: THREE.BufferGeometry;
  const [sx, sy, sz] = p.s;
  if (p.shape === "cyl") {
    g = new THREE.CylinderGeometry(sx / 2 * (p.taper ?? 1), sx / 2, sy, lowSpec ? 6 : 9, 1, false);
  } else if (p.shape === "eye") {
    // arch stays inside its s-box: base at -sy/2, crown at most +sy/2
    const tube = sz / 2, R = Math.min(sx / 2 - tube, sy - 2 * tube);
    g = new THREE.TorusGeometry(R, tube, 4, 8, Math.PI);
    g.translate(0, -sy / 2 + tube, 0);
  } else if (p.shape === "rbox" && !lowSpec) {
    g = new RoundedBoxGeometry(sx, sy, sz, 2, Math.min(0.06, Math.min(sx, sy, sz) * 0.3));
  } else {
    g = new THREE.BoxGeometry(Math.max(sx, 0.003), Math.max(sy, 0.003), Math.max(sz, 0.003));
  }
  if (p.cell) {
    // atlas cell on every face (only the +z front is ever really seen)
    const r = CELLS[p.cell];
    const uv = g.getAttribute("uv");
    for (let i = 0; i < uv.count; i++) uv.setXY(i, (r[0] + uv.getX(i) * (r[2] - r[0])) / 2048, 1 - (r[3] - uv.getY(i) * (r[3] - r[1])) / 2048);
  }
  const l = p.l ?? [0, 0, 0];
  if (l[0] || l[1] || l[2]) g.translate(l[0], l[1], l[2]);
  tmpE.set(p.rx ?? 0, p.ry ?? 0, p.rz ?? 0, "YXZ");
  tmpQ.setFromEuler(tmpE);
  tmpM.compose(tmpV.set(p.o[0], p.o[1], p.o[2]), tmpQ, ONE);
  g.applyMatrix4(tmpM);
  const tile = p.mat === "hazard" ? 0.6 : p.mat === "straw" ? 0.9 : p.mat === "wood" ? 1.2 : p.mat === "stand" ? 2.5 : p.mat === "galv" ? 1.4 : 0;
  if (tile) projUV(g, tile, p.o[0], p.o[1], p.o[2]);
  tmpC.setHex(p.color);
  const n = g.getAttribute("position").count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { col[i * 3] = tmpC.r; col[i * 3 + 1] = tmpC.g; col[i * 3 + 2] = tmpC.b; }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  const ng = g.index ? g.toNonIndexed() : g;
  if (ng !== g) g.dispose();
  return ng;
}

function floorPaint(fp: FloorPaint, lowSpec: boolean): THREE.Mesh {
  const W = fp.x1 - fp.x0, D = fp.z1 - fp.z0;
  const ppm = Math.min(lowSpec ? 22 : 42, (lowSpec ? 1024 : 2048) / Math.max(W, D));
  const cw = Math.round(W * ppm), ch = Math.round(D * ppm);
  const [c, g] = canvas(cw, ch);
  const X = (x: number): number => (x - fp.x0) * ppm, Z = (z: number): number => (z - fp.z0) * ppm;
  g.lineCap = "butt";
  for (const p of fp.prims) {
    g.save();
    if (p.t === "line") {
      g.strokeStyle = p.col; g.lineWidth = p.w * ppm;
      if (p.dash) g.setLineDash([p.dash * ppm, p.dash * 0.8 * ppm]);
      g.beginPath(); g.moveTo(X(p.a[0]), Z(p.a[1])); g.lineTo(X(p.b[0]), Z(p.b[1])); g.stroke();
    } else if (p.t === "circle") {
      g.strokeStyle = p.col; g.lineWidth = p.w * ppm;
      g.beginPath(); g.arc(X(p.c[0]), Z(p.c[1]), p.r * ppm, p.a0 ?? 0, p.a1 ?? Math.PI * 2); g.stroke();
    } else if (p.t === "disc") {
      g.fillStyle = p.col; g.beginPath(); g.arc(X(p.c[0]), Z(p.c[1]), p.r * ppm, 0, Math.PI * 2); g.fill();
    } else if (p.t === "hatch") {
      g.beginPath(); g.rect(X(p.x0), Z(p.z0), (p.x1 - p.x0) * ppm, (p.z1 - p.z0) * ppm); g.clip();
      g.strokeStyle = p.col; g.lineWidth = p.w * ppm;
      const span = (p.x1 - p.x0) + (p.z1 - p.z0);
      for (let d = 0; d < span; d += p.gap) {
        g.beginPath(); g.moveTo(X(p.x0 + d), Z(p.z0)); g.lineTo(X(p.x0 + d - (p.z1 - p.z0)), Z(p.z1)); g.stroke();
      }
    } else {
      g.translate(X(p.c[0]), Z(p.c[1])); g.rotate(p.rot);
      g.fillStyle = p.col; g.font = `900 ${Math.round(p.size * ppm)}px Arial Black, Arial, sans-serif`;
      g.textAlign = "center"; g.textBaseline = "middle"; g.fillText(p.s, 0, 0);
    }
    g.restore();
  }
  // wear: scuffed-off flecks and tyre-scrub streaks
  const r = rng(13);
  g.globalCompositeOperation = "destination-out";
  for (let k = 0; k < (cw * ch) / 90; k++) {
    g.fillStyle = `rgba(0,0,0,${0.25 + r() * 0.6})`;
    g.fillRect(r() * cw, r() * ch, 1 + r() * 3, 1 + r() * 3);
  }
  g.globalCompositeOperation = "source-over";
  const t = tex(c, false);
  t.userData.shared = false;
  const geo = new THREE.PlaneGeometry(W, D);
  geo.rotateX(-Math.PI / 2);
  geo.translate((fp.x0 + fp.x1) / 2, fp.y, (fp.z0 + fp.z1) / 2);
  const mat = new THREE.MeshStandardMaterial({
    map: t, transparent: true, depthWrite: false, roughness: 0.85, metalness: 0,
    polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = "ak:floorPaint";
  mesh.receiveShadow = true;
  mesh.renderOrder = 1;
  return mesh;
}

export function buildKit(ctx: MapExtrasContext, layout: KitLayout, name: string): THREE.Object3D | null {
  if (!layout.pieces.length && !layout.floor) return null;
  const groups = new Map<KMat, THREE.BufferGeometry[]>();
  for (const p of layout.pieces) {
    const g = pieceGeo(p, ctx.lowSpec);
    (groups.get(p.mat) ?? groups.set(p.mat, []).get(p.mat)!).push(g);
  }
  const off = { polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 };
  const mats: Record<KMat, () => THREE.Material> = {
    galv: () => new THREE.MeshStandardMaterial(surfaceMaps("galv", { color: 0xffffff, vertexColors: true, roughness: 0.62, metalness: 0.45 }, 0.8)),
    paint: () => new THREE.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.7, metalness: 0.02, ...off }),
    hazard: () => new THREE.MeshStandardMaterial({ map: hazard(), color: 0xffffff, vertexColors: true, roughness: 0.78, metalness: 0, ...off }),
    lamp: () => new THREE.MeshBasicMaterial({ color: 0xfff3d6, toneMapped: false }),
    ads: () => {
      const a = atlas();
      return new THREE.MeshStandardMaterial({ map: a, emissiveMap: a, emissive: 0xffffff, emissiveIntensity: ctx.theme === "day" ? 0.32 : 0.8, roughness: 0.45, metalness: 0, ...off });
    },
    stand: () => new THREE.MeshStandardMaterial(surfaceMaps("concrete", { color: 0xffffff, vertexColors: true, roughness: 0.95, metalness: 0 }, 0.7)),
    wood: () => new THREE.MeshStandardMaterial(surfaceMaps("wood", { color: 0xffffff, vertexColors: true, roughness: 0.9, metalness: 0 }, 0.7)),
    straw: () => {
      const s = straw();
      return new THREE.MeshStandardMaterial({ map: s.map, normalMap: s.normal, color: 0xffffff, vertexColors: true, roughness: 0.96, metalness: 0 });
    },
  };
  const root = new THREE.Group();
  root.name = name;
  for (const [mat, geos] of groups) {
    const merged = mergeGeometries(geos);
    for (const g of geos) g.dispose();
    if (!merged) { console.error(`[${name}] merge failed for ${mat}`); continue; }
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, mats[mat]());
    mesh.name = `ak:${mat}`;
    mesh.castShadow = ctx.shadows && (mat === "galv" || mat === "stand" || mat === "wood" || mat === "straw");
    mesh.receiveShadow = mat !== "lamp";
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    root.add(mesh);
  }
  if (layout.floor) {
    const fm = floorPaint(layout.floor, ctx.lowSpec);
    fm.matrixAutoUpdate = false;
    fm.updateMatrix();
    root.add(fm);
  }
  return root;
}
